import { configureStore } from '@reduxjs/toolkit'
import { getLocalStorageValue, removeLocalStorageValue } from './localstorage'
import { dataFetcher } from './datafetcher'
import { setRemoteUnauthorizedHandler } from './request'
import {
    asyncConfigureSensors,
    asyncScanSensors,
    asyncGetSensorsData,
    asyncPostPwr,
    isNoData,
    selectedMetricsKey,
    sensorsSlice } from './sensors'
import {
    loginSlice,
    asyncDoLogin,
    asyncRegisterUser,
    loginTokenKey,
    setToken,
    sessionExpired } from './login'

import { activitiesSlice, asyncReqActivities } from './activities'
import { workoutsSlice, asyncReqWorkouts, asyncCreateWorkout, asyncSaveLiveActivity } from './workouts'
import { plansSlice, asyncCreatePlans, asyncMovePlan, asyncDeletePlan, startPlanRangeRequest, startPlannerActivitiesRequest, plannerActivitiesChanged } from './plans'
import { sessionSlice, invalidateSession } from './session'
import { asyncReqUserGeneralInfo, userinfoSlice } from './userinfo'
import { usermeasuresSlice } from './usermeasures'
import { asyncChangePassword, passwordSlice } from './password'
import { asyncRecoverPassword, asyncResetPassword, recoverySlice } from './recovery'
import { asyncGetSensorsStatus, asyncGetUsbDevices, asyncSetUsbDevice, connectivitySlice } from './connectivity'
import { asyncCheckDaemonCompatibility, assertDaemonCompatible as assertDaemonCompatibleWith } from './daemonCompatibility'
import { PENDING_WORKOUT_KEY } from '../workoutCapture'
    
const reducer = {
    sensors: sensorsSlice.reducer,
    login: loginSlice.reducer,
    session: sessionSlice.reducer,
    userinfo: userinfoSlice.reducer,
    usermeasures: usermeasuresSlice.reducer,
    activities: activitiesSlice.reducer,
    workouts: workoutsSlice.reducer,
    plans: plansSlice.reducer,
    password: passwordSlice.reducer,
    recovery: recoverySlice.reducer,
    connectivity: connectivitySlice.reducer
    //TODO: add mode: training vs. browsing
}

export const store = configureStore({ reducer })

// On a remote 401 (expired/revoked JWT) clear only the session: unlike
// logoutUser() this deliberately leaves PENDING_WORKOUT_KEY and the player
// capture untouched so an in-progress ride survives and resolves on exit.
setRemoteUnauthorizedHandler(() => {
    removeLocalStorageValue(loginTokenKey)
    store.dispatch(sessionExpired())
    store.dispatch(invalidateSession())
})

// unwrap() rejects on failure so the resilient poller can distinguish a dead
// daemon (rejection → back off and keep re-trying) from a healthy one.
dataFetcher.init(() => store.dispatch(asyncGetSensorsData()).unwrap())

const RECONFIGURE_AFTER_EMPTY_POLLS = 3
const RECONFIGURE_COOLDOWN_MS = 10000
let emptyConfiguredPolls = 0
let lastAutoReconfigureAt = Date.now()

// The sensor channel assignment lives only in the daemon's memory and is lost
// when it restarts. Once a "configured" session keeps polling empty data (the
// daemon is alive but has no channels), re-assert the saved selection so the
// app recovers without a manual scan/configure. A fresh daemon closes nothing,
// so this completes in milliseconds on a restart (not the ~12 s warm-configure).
store.subscribe(() => {
    const sensors = store.getState().sensors
    if (!sensors || sensors.mode !== 'configured') {
        emptyConfiguredPolls = 0
        return
    }

    if (isNoData(sensors)) {
        emptyConfiguredPolls++
        if (emptyConfiguredPolls >= RECONFIGURE_AFTER_EMPTY_POLLS) {
            emptyConfiguredPolls = 0
            const now = Date.now()
            if (now - lastAutoReconfigureAt >= RECONFIGURE_COOLDOWN_MS) {
                lastAutoReconfigureAt = now
                const selection = sensors.selectedMetrics
                if (selection && selection.length) {
                    store.dispatch(asyncConfigureSensors(selection))
                }
            }
        }
    } else {
        emptyConfiguredPolls = 0
    }
})

// The /version verdict is otherwise only refreshed at view mount, so the
// outdated-daemon banner would outlive a daemon upgrade: status polling and
// the manual «Проверить» fetch /status alone. Re-grading on every successful
// status response (lastChecked advancing, or returning to reachable for
// status-less bodies) clears the banner once the updated daemon answers —
// no reload needed.
let prevReachable = store.getState().connectivity.reachable
let prevLastChecked = store.getState().connectivity.lastChecked
store.subscribe(() => {
    const { reachable, lastChecked } = store.getState().connectivity
    const refreshed = reachable === true
        && (reachable !== prevReachable || lastChecked !== prevLastChecked)
    prevReachable = reachable
    prevLastChecked = lastChecked
    if (refreshed)
        store.dispatch(asyncCheckDaemonCompatibility())
})

// A login token transition marks a session boundary: logout, expiration, or a
// successful login/session replacement. User-scoped remote slices reset on
// sessionInvalidated and ignore responses captured under an older generation.
let prevLoginToken = store.getState().login.token
store.subscribe(() => {
    const token = store.getState().login.token
    if (token !== prevLoginToken) {
        prevLoginToken = token
        if (token) store.dispatch(invalidateSession())
    }
})

// The planner's activity summaries are invalidated whenever the diary's
// activity list is replaced (upload/save/delete/rename flows all rebuild the
// list). The first diary load (undefined → list) is skipped so an unrelated
// initial fetch does not spuriously invalidate a loaded planner.
let prevActivitiesRef = store.getState().activities.activities
store.subscribe(() => {
    const activitiesRef = store.getState().activities.activities
    if (activitiesRef !== prevActivitiesRef) {
        const wasUndefined = prevActivitiesRef === undefined
        prevActivitiesRef = activitiesRef
        if (!wasUndefined || activitiesRef !== undefined) {
            store.dispatch(plannerActivitiesChanged())
        }
    }
})

export function fetchSensors() {
    store.dispatch(asyncScanSensors())
}

export function fetchStatus() {
    store.dispatch(asyncGetSensorsStatus())
}

export function checkDaemonCompatibility() {
    store.dispatch(asyncCheckDaemonCompatibility())
}

export async function assertDaemonCompatible() {
    return assertDaemonCompatibleWith(store.dispatch)
}

export function fetchUsbDevices() {
    store.dispatch(asyncGetUsbDevices())
}

export function setUsbDevice(id) {
    return store.dispatch(asyncSetUsbDevice(id))
}

let statusPollTimer = null

export function startStatusPolling(ms) {
    stopStatusPolling()
    fetchStatus()
    statusPollTimer = setInterval(() => {
        store.dispatch(asyncGetSensorsStatus())
    }, ms || 5000)
}

export function stopStatusPolling() {
    if (statusPollTimer) {
        clearInterval(statusPollTimer)
        statusPollTimer = null
    }
}

export function configureSensors(metrics) {
    store.dispatch(asyncConfigureSensors(metrics))
}

export function postPwr(data) {
    return store.dispatch(asyncPostPwr(data))
}

export function startWork() {
    const selectedMetrics = getLocalStorageValue(selectedMetricsKey)
    if (selectedMetrics) {
        configureSensors(selectedMetrics)
    } else {
        fetchSensors()
    }
}

export function checkAuthorizationStatus() {
    const loginToken = getLocalStorageValue(loginTokenKey)
    if (loginToken) {
        const { expiration, token } = loginToken
        if (expiration > Date.now()) {
            store.dispatch(setToken(token))
        } else {
            removeLocalStorageValue(loginTokenKey)
            store.dispatch(invalidateSession())
        }
    }
}

export function logoutUser() {
    removeLocalStorageValue(loginTokenKey)
    removeLocalStorageValue(PENDING_WORKOUT_KEY)
    store.dispatch(setToken(''))
    store.dispatch(invalidateSession())
}

export function doLogin(data) {
    store.dispatch(asyncDoLogin(data))
}

export function getLoginToken() {
    const state = store.getState()
    if (state.login)
        return state.login.token
    else
        return ''
}

export function registerUser(data) {
    store.dispatch(asyncRegisterUser(data))
}

export function changePassword(data) {
    store.dispatch(asyncChangePassword(data))
}

export function recoverPassword(data) {
    store.dispatch(asyncRecoverPassword(data))
}

export function resetPassword(data) {
    store.dispatch(asyncResetPassword(data))
}

export function requestMyActivities(data) {
    store.dispatch(asyncReqActivities(data))
}

export function requestWorkouts() {
    store.dispatch(asyncReqWorkouts())
}

export function createWorkout(data) {
    return store.dispatch(asyncCreateWorkout(data))
}

export function saveLiveActivity(data) {
    return store.dispatch(asyncSaveLiveActivity(data))
}

export function requestUserInfoGeneral(data) {
    const userinfo = store.getState().userinfo
    if ((!userinfo.info || !userinfo.info.first_name) && userinfo.infoIsLoading === false) {
        store.dispatch(asyncReqUserGeneralInfo(data))
    }
}

// --- workout planner -------------------------------------------------------

export function requestPlans(range) {
    return store.dispatch(startPlanRangeRequest(range))
}

export function createPlans(items) {
    if (store.getState().plans.createStatus === 'pending') {
        return Promise.reject(new Error('Создание уже выполняется'))
    }
    return store.dispatch(asyncCreatePlans(items)).unwrap()
}

export function patchPlan(id, date) {
    return store.dispatch(asyncMovePlan({ id, date })).unwrap()
}

export function deletePlan(id) {
    return store.dispatch(asyncDeletePlan(id)).unwrap()
}

export function requestPlannerActivities(range) {
    return store.dispatch(startPlannerActivitiesRequest(range))
}

export function setActivePlanRange(range) {
    store.dispatch(plansSlice.actions.setActivePlanRange(range))
}

export function setActiveActivityRange(range) {
    store.dispatch(plansSlice.actions.setActiveActivityRange(range))
}

export function clearPlanMutation(planId) {
    store.dispatch(plansSlice.actions.clearPlanMutation(planId))
}

export function resetPlansCreateStatus() {
    store.dispatch(plansSlice.actions.resetCreateStatus())
}